// ---------------------------------------------------------------------------
// Alert Renderer
// ---------------------------------------------------------------------------
// Foundational primitive — publishes the editor `Alert` block as a
// semantic <aside role="alert"> banner. Variant maps to a known set so
// CSS class selectors stay closed.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

type AlertVariant = "info" | "warning" | "success" | "error";

interface AlertProps {
  message?: string;
  variant?: AlertVariant | string;
}

const VALID_VARIANTS: ReadonlySet<AlertVariant> = new Set([
  "info",
  "warning",
  "success",
  "error",
]);

const ICONS: Record<AlertVariant, string> = {
  info: "ℹ",
  warning: "⚠",
  success: "✓",
  error: "✗",
};

const ROLE_BY_VARIANT: Record<AlertVariant, string> = {
  info: "status",
  warning: "alert",
  success: "status",
  error: "alert",
};

function resolveVariant(value: unknown): AlertVariant {
  if (typeof value === "string" && VALID_VARIANTS.has(value as AlertVariant)) {
    return value as AlertVariant;
  }
  return "info";
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as AlertProps;

  const message = typeof p.message === "string" ? p.message.trim() : "";
  if (!message) {
    return "<!-- alert missing message -->";
  }

  const variant = resolveVariant(p.variant);
  const icon = ICONS[variant];
  const role = ROLE_BY_VARIANT[variant];

  return `<section class="ease-section ease-alert ease-alert--${attr(variant)}" role="${attr(role)}">
  <div class="ease-alert__inner">
    <span class="ease-alert__icon" aria-hidden="true">${text(icon)}</span>
    <p class="ease-alert__message">${text(message)}</p>
  </div>
</section>`;
}

registerRenderer("alert", { render });
